import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { PatientFacade } from './patient.facade';
import { Observable, of } from 'rxjs';

import {
  map,
  switchMap,
  withLatestFrom,
  catchError,
  tap,
} from 'rxjs/operators';

import {
  PatientActionTypes,
  SearchPatients,
  SearchPatientsSuccess,
  SearchPatientsError,
  GetPatientDetail,
  GetPatientDetailSuccess,
  GetPatientDetailError,
  GetCostEstimatorDetail,
  GetCostEstimatorDetailSuccess,
  GetCostEstimatorDetailError,
  PatientEstimateCost,
  PatientEstimateCostSuccess,
  PatientEstimateCostError,
  GetServiceProcedures,
  GetServiceProceduresSuccess,
  GetServiceProceduresError,
  InactiveMemberSearch,
  InactiveMemberSearchSuccess,
  InactiveMemberSearchError,
  GetProviderGroup,
  GetProviderGroupSuccess,
  GetProviderGroupError,
  GetProviders,
  GetProvidersSuccess,
  GetProvidersError,
  GetProviderDetails,
  GetProviderDetailsSuccess,
  GetProviderDetailsError,
  GetInOutNetworkDetails,
  GetInOutNetworkDetailsSuccess,
  GetInOutNetworkDetailsError,
  GetPharmacyFacets,
  GetPharmacyFacetsSuccess,
  GetPharmacyFacetsError,
  OpenAccordionDetails,
  GetSureFitDetails,
  SureFitSuccess,
  SureFitError,
  GetPrecertUrlSuccess,
  GetPrecertUrlError,
  GetPathwellInOutNetworkDetails,
} from './patient.actions';

import { PatientService } from '../patient/patient.service';

import { ID_SSN_DISPLAY_TEXT } from '@cigna/chcp/patient/shared/util';
import { deepestActivatedRoute } from '@cigna/chcp/shared/util';

import {
  ProviderService,
  ProviderByGroup,
  ProviderByTin,
} from '@cigna/chcp/shared/provider-data-access';

import { UserFacade } from '@cigna/chcp/shared/user-profile-data-access';
import { Router } from '@angular/router';
import { ShellTrackHandlers } from '@cigna/chcp/shared/analytics-util';
import { AuthFacade } from '@cigna/chcp/auth/data-access';
import { parse, differenceInDays, isBefore, isToday } from 'date-fns';
import { FeatureTogglesFacade } from '@cigna/features';

const apiReponseAnalyticsLabel = 'API Search Response';

@Injectable()
export class PatientEffects {
  search$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PatientActionTypes.SearchPatients),
      map((action: SearchPatients) => action.payload),
      withLatestFrom(this.authFacade.lob$),
      switchMap(([payload, lob]) => {
        const requestBody = {
          eligibilityAsOfDate: payload.asOfDate,
          coverageType: lob === 'DEN' ? 'DENT' : 'MED',
          memberDataList: payload.patients.map((patient) => ({
            firstName: patient.patientFirstName,
            lastName: patient.patientLastName,
            dateOfBirth: patient.patientDob,
            id: patient.patientId,
          })),
        };
        return this.patientService.searchMultipleMembers(requestBody).pipe(
          map((data: any) => {
            this.router.navigate(['patient/results']);
            return new SearchPatientsSuccess(data);
          }),
          catchError((err) => of(new SearchPatientsError(err))),
        );
      }),
    ),
  );

  trackSearch$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PatientActionTypes.SearchPatients),
        map((action: SearchPatients) => action.payload),
        tap((payload) => {
          const event121Count = payload.patients.filter(
            (patient) =>
              patient.patientId &&
              patient.patientDob &&
              !patient.patientFirstName &&
              !patient.patientLastName,
          ).length;
          const event122Count = payload.patients.filter(
            (patient) =>
              patient.patientId &&
              !patient.patientDob &&
              patient.patientFirstName &&
              patient.patientLastName,
          ).length;
          const event123Count = payload.patients.filter(
            (patient) =>
              !patient.patientId &&
              patient.patientDob &&
              patient.patientFirstName &&
              patient.patientLastName,
          ).length;
          const event124Count = payload.patients.filter(
            (patient) =>
              patient.patientId &&
              patient.patientDob &&
              patient.patientFirstName &&
              patient.patientLastName,
          ).length;

          const asofparsed = parse(payload.asOfDate, 'yyyy-MM-dd', new Date());
          const dayCount =
            isBefore(asofparsed, new Date()) && !isToday(asofparsed)
              ? `-${differenceInDays(new Date(), asofparsed)}`
              : `${differenceInDays(asofparsed, new Date())}`;

          let eventsString = '';
          eventsString = (
            event121Count
              ? eventsString.concat(`event121=${event121Count}`)
              : ''
          )
            .concat(`,event465=${dayCount}`)
            .concat(event122Count ? `,event122=${event122Count}` : '')
            .concat(event123Count ? `,event123=${event123Count}` : '')
            .concat(event124Count ? `,event124=${event124Count}` : '');

          this.analytics.trackEventOther({}, 'Search', 'ac-PS006-Search Info', {
            events: eventsString,
            eVar111: `patient search count:${payload.patients.length}`,
            eVar143: dayCount,
          });
        }),
      ),
    { dispatch: false },
  );

  trackSearchPatientsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PatientActionTypes.SearchPatientsSuccess),
        map((action: SearchPatientsSuccess) => action.payload),
        tap((payload) => {
          if (payload && payload.patients) {
            this.analytics.trackEventOther(
              {},
              apiReponseAnalyticsLabel,
              'ac-PS008-Search Outcome',
              {
                events: `event160,event430=${payload.patients.length}`,
                list1: payload.patients
                  .map((patient) => patient.detail.coverage.groupNumber)
                  .filter((v, i, a) => a.indexOf(v) === i)
                  .join(','),
              },
            );
          }
        }),
      ),
    { dispatch: false },
  );

  trackSearchPatientsServiceError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PatientActionTypes.SearchPatientsError),
        map((action: SearchPatientsError) => action.error),
        tap((error) => {
          if (error.status === 404) {
            this.analytics.trackEventOther(
              {},
              apiReponseAnalyticsLabel,
              'ac-PS008-Search Outcome',
              {
                events: `event161,event430=0`,
              },
            );
          } else {
            this.analytics.trackServiceError(
              {},
              'chcp/benefits/v2/search/individuals:POST',
              error,
              'Error searching patients.',
            );
          }
        }),
      ),
    { dispatch: false },
  );

  inactivesearch$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PatientActionTypes.InactiveMemberSearch),
      map((action: InactiveMemberSearch) => action.payload),
      withLatestFrom(this.authFacade.lob$),
      switchMap(([payload, lob]) => {
        const uniqueIdentifier = payload.uniqueIdentifier;
        const requestBody = {
          eligibilityAsOfDate: payload.asOfDate,
          coverageType: lob === 'DEN' ? 'DENT' : 'MED',
          memberDataList: []
            .concat(payload.patient as any)
            .map((patient: any) => ({
              firstName: patient.patientFirstName,
              lastName: patient.patientLastName,
              dateOfBirth: patient.patientDob,
              id:
                patient.patientId === ID_SSN_DISPLAY_TEXT
                  ? ''
                  : patient.patientId,
            })),
        };
        return this.patientService.searchSingleMember(requestBody).pipe(
          map(
            (data) =>
              new InactiveMemberSearchSuccess({
                uniqueIdentifier,
                display: data.patients[0].display,
                detail: data.patients[0].detail,
              }),
          ),
          catchError(() => of(new InactiveMemberSearchError(''))),
        );
      }),
    ),
  );

  getPatientDetail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PatientActionTypes.GetPatientDetail),
      map((action: GetPatientDetail) => action.data),
      switchMap((payload) =>
        this.patientService.getPatientDetail(payload).pipe(
          map((data) => new GetPatientDetailSuccess(data)),
          catchError((err) => of(new GetPatientDetailError(err))),
        ),
      ),
    ),
  );

  trackPatientDetailServiceError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PatientActionTypes.GetPatientDetailError),
        map((action: GetPatientDetailError) => action.error),

        tap((error) => {
          this.analytics.trackServiceError(
            {},
            'chcp/patient/v2/benefits/{compositeKey}:GET',
            error,
            'Error loading patient detail.',
          );
        }),
      ),
    { dispatch: false },
  );

  getPharmacyFacetDetails$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PatientActionTypes.GetPharmacyFacets),
      map((action: GetPharmacyFacets) => action.payload),
      withLatestFrom(
        this.patientFacade.coverageDetail$,
        this.patientFacade.patientDetail$,
      ),
      switchMap(([payload, coverage, patient]) =>
        this.patientService
          .getPharmacyFacetDetails(payload, {
            erisaApplicable: !!coverage?.erisaApplicable,
            carveOutVendorName: coverage?.plan?.rxCarveOutVendor || '',
            internalProcessingId: patient?.details?.internalProcessingId || '',
            benefitOptionCode: coverage?.plan.benefitOption || '',
          })
          .pipe(
            map((data) => new GetPharmacyFacetsSuccess(data)),
            catchError((err) => of(new GetPharmacyFacetsError(err))),
          ),
      ),
    ),
  );

  /* eslint-disable sonarjs/cognitive-complexity */
  getProviderGroup$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PatientActionTypes.GetProviderGroup),
      map((action: GetProviderGroup) => action.payload),
      withLatestFrom(
        this.patientFacade.patientDetail$,
        this.patientFacade.coverageDetail$,
        this.featureToggleFacade.featuresEnabled(['pathwellUpdates']),
      ),
      switchMap(([payload, patient, coverageDetail, isPathwell]) =>
        this.userFacade.tinLob$(payload).pipe(
          map((lob) => ({
            tin: payload,
            lob,
            patient,
            coverageDetail,
            isPathwell,
          })),
        ),
      ),
      switchMap(({ tin, lob, patient, coverageDetail, isPathwell }) =>
        this.providerService.getProvidersByTin(tin, lob).pipe(
          switchMap((res: ProviderByTin[]) => {
            if (res.length === 1) {
              if (
                res[0].type &&
                (res[0].type === 'AS' || res[0].type.toLowerCase() === 'group')
              ) {
                return of(
                  new GetProviders([res[0].id]),
                  new GetProviderGroupSuccess(res),
                );
              }
              const requestData = {
                netId: '',
                netType: '',
                zip: '',
                provId: res[0].id,
              };
              if (
                coverageDetail &&
                coverageDetail.networkDetails &&
                patient &&
                patient.details &&
                patient.details.addresses &&
                patient.details.addresses[0]
              ) {
                requestData.netId = coverageDetail.networkDetails.networkId;
                requestData.netType = coverageDetail.networkDetails.networkType;
                requestData.zip = patient.details.addresses[0].zip;
              }
              return of(
                isPathwell &&
                  coverageDetail?.plan?.pathwellIndicator === 'Active'
                  ? new GetPathwellInOutNetworkDetails({
                      payload: {
                        healthPlanId: requestData.netId,
                        tin,
                        clientProviderId: res[0].id,
                        networkDetails: 'N',
                        dateOfService: new Date().toISOString().split('T')[0],
                        provId: requestData.provId,
                      },
                      isIndemnity: !!(
                        coverageDetail &&
                        coverageDetail.plan &&
                        coverageDetail.plan.isIndemnity
                      ),
                    })
                  : new GetInOutNetworkDetails({
                      payload: requestData,
                      isIndemnity: !!(
                        coverageDetail &&
                        coverageDetail.plan &&
                        coverageDetail.plan.isIndemnity
                      ),
                    }),
                new GetProviderDetails({
                  provId: res[0].id,
                  tin: res[0].tinId,
                }),
                new GetProviderGroupSuccess(res),
              );
            }
            return of(new GetProviderGroupSuccess(res));
          }),
          catchError((err) => of(new GetProviderGroupError(err))),
        ),
      ),
    ),
  );

  getProviders$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PatientActionTypes.GetProviders),
      map((action: GetProviders) => action.payload),
      withLatestFrom(
        this.patientFacade.patientDetail$,
        this.patientFacade.coverageDetail$,
        this.patientFacade.getProviders$,
        this.patientFacade.medAmInNetworkSelectedTin$,
        this.featureToggleFacade.featuresEnabled(['pathwellUpdates']),
      ),
      switchMap(
        ([
          payload,
          patient,
          coverageDetail,
          providers,
          seletedTin,
          isPathwell,
        ]) =>
          this.providerService.getProvidersByGroup(payload).pipe(
            switchMap((res: ProviderByGroup[]) => {
              const filteredGroups = res.filter(
                (value: any) =>
                  value.type &&
                  (value.type === 'AS' || value.type.toLowerCase() === 'group'),
              );
              const filteredProviders = res.filter(
                (value: any) =>
                  value.type &&
                  value.type !== 'AS' &&
                  value.type.toLowerCase() !== 'group',
              );

              const filteredGroupIds = filteredGroups.map((group) => group.id);
              const provIds = providers.data
                ? providers.data.map((prov: any) => prov.id)
                : [];
              const filteredProviderids = filteredProviders.filter(
                (provider) => !provIds.includes(provider.id),
              );
              const mergedProvidersList = [
                ...(providers.data ? providers.data : []),
                ...filteredProviderids,
              ];
              if (filteredGroupIds.length) {
                return [new GetProviders(filteredGroupIds)];
              }
              if (
                mergedProvidersList.length === 1 &&
                mergedProvidersList[0].type !== 'AS'
              ) {
                const requestData = {
                  netId: '',
                  netType: '',
                  zip: '',
                  provId: mergedProvidersList[0].id,
                };
                if (
                  coverageDetail &&
                  coverageDetail.networkDetails &&
                  patient &&
                  patient.details &&
                  patient.details.addresses &&
                  patient.details.addresses[0]
                ) {
                  requestData.netId = coverageDetail.networkDetails.networkId;
                  requestData.netType =
                    coverageDetail.networkDetails.networkType;
                  requestData.zip = patient.details.addresses[0].zip;
                }
                return [
                  isPathwell &&
                  coverageDetail?.plan?.pathwellIndicator === 'Active'
                    ? new GetPathwellInOutNetworkDetails({
                        payload: {
                          healthPlanId: requestData.netId,
                          tin: seletedTin,
                          clientProviderId: mergedProvidersList[0].id,
                          networkDetails: 'N',
                          dateOfService: new Date().toISOString().split('T')[0],
                          provId: requestData.provId,
                        },
                        isIndemnity: !!(
                          coverageDetail &&
                          coverageDetail.plan &&
                          coverageDetail.plan.isIndemnity
                        ),
                      })
                    : new GetInOutNetworkDetails({
                        payload: requestData,
                        isIndemnity: !!(
                          coverageDetail &&
                          coverageDetail.plan &&
                          coverageDetail.plan.isIndemnity
                        ),
                      }),
                  new GetProviderDetails({
                    provId: mergedProvidersList[0].id,
                    tin: mergedProvidersList[0].tinId || seletedTin,
                  }),
                  new GetProvidersSuccess(mergedProvidersList),
                ];
              }
              return [new GetProvidersSuccess(mergedProvidersList)];
            }),
            catchError((err) => of(new GetProvidersError(err))),
          ),
      ),
    ),
  );

  getInOutNetworkDetails$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PatientActionTypes.GetInOutNetworkDetails),
      map((action: GetInOutNetworkDetails) => action.data),
      switchMap((value) => {
        if (!value.isIndemnity) {
          return this.providerService
            .getProviderNetworkVerification(value.payload)
            .pipe(
              map((data) => new GetInOutNetworkDetailsSuccess(data)),
              catchError((err) => of(new GetInOutNetworkDetailsError(err))),
            );
        }
        return of(new GetInOutNetworkDetailsSuccess('Indemnity'));
      }),
    ),
  );

  getPathwellInOutNetworkDetails$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PatientActionTypes.GetPathwellInOutNetworkDetails),
      map((action: GetPathwellInOutNetworkDetails) => action.data),
      switchMap((value) => {
        if (!value.isIndemnity) {
          return this.providerService
            .getPathwellProviderNetworkVerification(value.payload)
            .pipe(
              map((data) => new GetInOutNetworkDetailsSuccess(data)),
              catchError((err) => of(new GetInOutNetworkDetailsError(err))),
            );
        }
        return of(new GetInOutNetworkDetailsSuccess('Indemnity'));
      }),
    ),
  );

  getPatientCoceDetail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PatientActionTypes.GetCostEstimatorDetail),

      map((action: GetCostEstimatorDetail) => ({
        key: action.key,
        type: action.patientType,
        network: action.network,
      })),
      withLatestFrom(this.patientFacade.patientAsOfDate$),
      switchMap(([payload, asOfDate]) =>
        this.patientService
          .getPatientCoceDetail(
            payload.key,
            payload.type,
            payload.network,
            asOfDate,
          )
          .pipe(
            map((data) => new GetCostEstimatorDetailSuccess(data, payload.key)),
            catchError((err) => of(new GetCostEstimatorDetailError(err))),
          ),
      ),
    ),
  );

  getProviderDetails$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PatientActionTypes.GetProviderDetails),
      map((action: GetProviderDetails) => action.payload),
      switchMap((payload) =>
        this.providerService
          .getProviderDetails(payload.provId, payload.tin)
          .pipe(
            map((data) => new GetProviderDetailsSuccess(data)),
            catchError((err) => of(new GetProviderDetailsError(err))),
          ),
      ),
    ),
  );

  getServiceProcedures$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PatientActionTypes.GetServiceProcedures),
      map((action: GetServiceProcedures) => action.payload),
      switchMap((payload) =>
        this.patientService.getServiceProcedures(payload).pipe(
          map((data) => new GetServiceProceduresSuccess(data, payload)),
          catchError((err) => of(new GetServiceProceduresError(err))),
        ),
      ),
    ),
  );

  patientEstimateCost$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PatientActionTypes.PatientEstimateCost),
      map((action: PatientEstimateCost) => action.payload),
      withLatestFrom(
        this.patientFacade.getPatientCompositeKey$,
        this.patientFacade.patientAsOfDate$,
      ),
      // TODO: resolve CHCP disables by bot
      // eslint-disable-next-line sonarjs/cognitive-complexity
      switchMap(([payload, key, asofdate]) => {
        if (!payload) {
          return of(new PatientEstimateCostSuccess([], payload));
        }

        const requestBody = {
          compositeKey: key,
          network: payload.network,
          asOfDate: asofdate,
          dos: payload.dateOfService,
          tin: payload.tin,
          type: payload.type,
          provider: {
            id: payload.providerOption?.id || payload.servicingProvider?.id,
            type:
              payload.providerOption?.type || payload.servicingProvider?.type,
            name:
              payload.providerOption?.name || payload.servicingProvider?.name,
          },
          includeAnesthesiology: payload.includeAnesthesiology,
          serviceTypes: payload.serviceProviderTypeList
            ? [
                {
                  posCode: payload.serviceProviderTypeList.posCode,
                  serviceTypeCode:
                    payload.serviceProviderTypeList.serviceTypeCode,
                  serviceTypeName:
                    payload.serviceProviderTypeList.serviceTypeName,
                  displayServiceTypeDesc:
                    payload.serviceProviderTypeList.displayServiceTypeDesc,
                  serviceTypeDesc:
                    payload.serviceProviderTypeList.serviceTypeDesc,
                  benefitName: payload.serviceProviderTypeList.benefitName,
                },
              ]
            : payload.services.map((service: any) => ({
                posCode: service.serviceType.posCode,
                serviceTypeCode: service.serviceType.serviceTypeCode,
                serviceTypeName: service.serviceType.serviceTypeName,
                displayServiceTypeDesc:
                  service.serviceType.displayServiceTypeDesc,
                serviceTypeDesc: service.serviceType.serviceTypeDesc,
                benefitName: service.serviceType.benefitName,
                placeOfServiceCode: service.serviceLocation
                  ? service.serviceLocation.code
                  : '',
                placeOfServiceName: service.serviceLocation
                  ? service.serviceLocation.description
                  : '',
              })),
          procedures: payload.procedureList
            ? payload.procedureList.map((obj: any) => ({
                code: obj.code,
                description: obj.description ? obj.description : '',
                units: obj.units,
                modifier1: obj.modifier1 ? obj.modifier1 : '',
                modifier2: obj.modifier2 ? obj.modifier2 : '',
              }))
            : [],
          clinicalCategory: payload.clinicalCategory
            ? payload.clinicalCategory.description
            : '',
          clinicalCategoryCode: payload.clinicalCategory
            ? payload.clinicalCategory.code
            : '',
          clinicalDescription: payload.codeDescription
            ? payload.codeDescription.description
            : '',
          clinicalDescriptionCode: payload.codeDescription
            ? payload.codeDescription.code
            : '',
        };
        return this.patientService.postEstimateCost(requestBody).pipe(
          map((data: any) => new PatientEstimateCostSuccess(data, requestBody)),
          catchError((err) => of(new PatientEstimateCostError(err))),
        );
      }),
    ),
  );

  patientEstimateCostSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PatientActionTypes.PatientEstimateCostSuccess),
        tap(() => {
          this.router.navigate(['estimate'], {
            queryParamsHandling: 'preserve',
            relativeTo: deepestActivatedRoute(this.router),
          });
        }),
      ),
    { dispatch: false },
  );

  trackPatientEstimateCostSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PatientActionTypes.PatientEstimateCostSuccess),
        map((action: PatientEstimateCostSuccess) => action.payload),
        tap((payload) => {
          // track PatientEstimateCostSuccess event
          if (payload) {
            this.analytics.trackEventOther(
              {},
              apiReponseAnalyticsLabel,
              'ac-PS031-Costs Outcome',
              {
                events: 'event205',
              },
            );
          }
        }),
      ),
    { dispatch: false },
  );

  trackatientEstimateCostServiceError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PatientActionTypes.PatientEstimateCostError),
        map((action: PatientEstimateCostError) => action.error),
        tap((error) => {
          if (error.status === 404) {
            this.analytics.trackEventOther(
              {},
              apiReponseAnalyticsLabel,
              'ac-PS031-Costs Outcome',
              {
                events: 'event206',
              },
            );
          } else {
            this.analytics.trackServiceError(
              {},
              'chcp/finance/v1/submitEstimateCost:POST',
              error,
              'Error loading cost of care estimate.',
            );
          }
        }),
      ),
    { dispatch: false },
  );

  backToSearch$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PatientActionTypes.ModifySearch, PatientActionTypes.NewSearch),
        map(() => {
          this.router.navigate(['patient/search']);
        }),
      ),
    { dispatch: false },
  );

  trackOpenAccordion$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PatientActionTypes.OpenAccordionDetails),
        map((action: OpenAccordionDetails) => action.key),
        tap((key) => {
          this.analytics.trackEventOther({}, key, 'ac-PS014-Accordion Open');
        }),
      ),
    { dispatch: false },
  );

  getSureFitDetails$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PatientActionTypes.GetSureFitDetails),
      map((action: GetSureFitDetails) => action.payload),
      switchMap((payload) =>
        this.patientService.getSureFitDetails(payload).pipe(
          map((data) => new SureFitSuccess(data)),
          catchError((err) => of(new SureFitError(err))),
        ),
      ),
    ),
  );

  getPrecertUrl$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PatientActionTypes.GetPrecertUrl),
      withLatestFrom(
        this.userFacade.firstName$,
        this.userFacade.lastName$,
        this.patientFacade.ohpTin$,
      ),
      switchMap(([, fname, lname, tin]) =>
        this.patientService.getPrecertUrl(fname, lname, tin).pipe(
          map((data) => new GetPrecertUrlSuccess(data)),
          catchError((err) => of(new GetPrecertUrlError(err))),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private userFacade: UserFacade,
    private authFacade: AuthFacade,
    private router: Router,
    private patientService: PatientService,
    private providerService: ProviderService,
    private patientFacade: PatientFacade,
    private analytics: ShellTrackHandlers,
    private featureToggleFacade: FeatureTogglesFacade,
  ) {}
}
